import { Trans, useTranslation } from "react-i18next";

import { Button, Illustration, Message, Spacer, Typography } from "@aviary";
import { useNavigate } from "@shared/react-router-dom";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import {
  Page,
  StoreLandingFooter,
  StoreLandingPageNavBar,
} from "@unauthenticated/signupSignin/components";
import type { PatientAttributes } from "@unauthenticated/signupSignin/data/types";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./PatientIntakePage.styles";

interface Props {
  isStoreOwner: boolean;
  isPreview: boolean;
  patientAttributes: PatientAttributes;
}

const PatientIntakePage = ({ isStoreOwner, isPreview, patientAttributes }: Props) => {
  const { storeSlug } = patientAttributes;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleOnSignUpClick = () => {
    navigate(`${authRoutes.welcome_sign_up(storeSlug)}?intake=true`);
  };

  return (
    <Page>
      <StoreLandingPageNavBar isPreview={isPreview} isStoreOwner={isStoreOwner} isShowOnDesktop />
      <div css={styles.contentContainer}>
        <div css={styles.column}>
          <Typography type="h1" css={styles.centerAlignedText}>
            {t(l.intake.welcome)}
          </Typography>
          <Typography type="body" css={styles.centerAlignedText}>
            {t(l.intake.welcomeDescription)}
          </Typography>
          <Illustration illustration="document" />
          <Spacer height="half" />
          <div css={styles.centerAlignedDesktopText}>
            <Typography type="h4">{t(l.intake.haveiPhone)}</Typography>
            <Typography isSecondaryWeight>{t(l.intake.stepOne)}</Typography>
            <Typography>{t(l.intake.stepOneDescription)}</Typography>
            <Button isOutlined isColor="system" onClick={handleOnSignUpClick}>
              {t(l.intake.toFullscriptCTA)}
            </Button>
            <Spacer height="one" />
            <Typography isSecondaryWeight>{t(l.intake.stepTwo)}</Typography>
            <Typography>{t(l.intake.stepTwoDescription)}</Typography>
            <div css={styles.dividerLine} />
          </div>
          <Spacer height="one" />
          <Message titleContent={t(l.intake.haveNoiPhone)} isColor="system">
            <Trans i18nKey={l.intake.haveNoiPhoneDescription}>
              Fullscript's <b>intake process</b> is currently only for iOS. Please connect with your
              practitioner for alternative intake options.
            </Trans>
          </Message>
        </div>
      </div>
      <StoreLandingFooter />
    </Page>
  );
};

export { PatientIntakePage };
